import { useQuery } from '@tanstack/react-query';
import { toast } from '@tyro/core/toaster';
import { type TFunction, useTranslation } from '@tyro/i18n';
import type { QueryAdditionalOptions } from '../@types';
import { getCmsConfig, useCmsConfig } from '../api/cms-config';
import { coreApiKeys } from '../api/keys';
import { queryClient } from '../query-client';

const cmsQuery = (
  path: string,
  { url, token }: { url: string; token: string },
  t?: TFunction<'common'[]>,
) => ({
  queryKey: coreApiKeys.cmsRequest(path),
  queryFn: async () => {
    try {
      const response = await fetch(`${url}${path}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.json();
    } catch (error) {
      if (t) {
        toast.error(t('common:snackbarMessages.errorFailed'));
      }
    }
  },
});

export async function getCmsRequest<TQueryFnData = unknown>(path: string) {
  const { composite_cmsConfig: config } = await getCmsConfig();
  return queryClient.fetchQuery<TQueryFnData>(cmsQuery(path, config!));
}

export function useCmsRequest<TQueryFnData = unknown>(
  path: string,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  const { t } = useTranslation(['common']);
  const { data: config } = useCmsConfig();
  const { url, token } = config ?? {};

  return useQuery<TQueryFnData>({
    ...cmsQuery(
      path,
      {
        url: url ?? '',
        token: token ?? '',
      },
      t,
    ),
    ...additionalOptions,
    enabled: Boolean(url && token && additionalOptions?.enabled !== false),
  });
}
